import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2, Shield } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentModal = ({ isOpen, onClose, cvData, cvId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  const handlePayment = async () => {
    if (!cvData.email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/payment/initialize`, {
        email: cvData.email,
        amount: 9.99,
        cvId: cvId,
      });

      if (response.data.status === "success") {
        const { payfast_url, form_data } = response.data.data;
        
        // Create and submit a form to PayFast
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = payfast_url;
        
        // Add all form fields
        Object.entries(form_data).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });
        
        // Append to body and submit
        document.body.appendChild(form);
        form.submit();
      }
    } catch (error) {
      toast.error("Payment initialization failed");
      console.error(error);
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-card border-border rounded-none">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            Complete Payment
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Secure payment via PayFast. One-time fee for your professional CV.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <div className="bg-secondary/30 p-4 border border-border/40 rounded-none">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-muted-foreground">CV Download</span>
              <span className="font-heading text-xl font-medium">$9.99</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Email: {cvData.email || "Not provided"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Amount in ZAR will be calculated at checkout
            </p>
          </div>

          <Button
            data-testid="pay-now-btn"
            onClick={handlePayment}
            disabled={loading || !cvData.email}
            className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-none h-12 font-medium tracking-wide transition-transform hover:-translate-y-0.5 active:scale-95"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Redirecting to PayFast...
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-4 w-4" />
                Pay with PayFast
              </>
            )}
          </Button>

          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Shield className="h-3 w-3" />
            <span>Secured by PayFast. Your payment is encrypted.</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
